'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/button';
import ErrorWithSupport from '@/components/support/ErrorWithSupport';
import { QueryKeys } from '@/hooks/api/query-keys';
import { checkDeviceClock, type DeviceClockStatus } from '@/lib/device-clock';

interface DeviceClockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: DeviceClockStatus;
  checking: boolean;
  error: boolean;
  stillSkewed: boolean;
  onCheck: () => void;
}

export function DeviceClockDialog({ open, onOpenChange, status, checking, error, stillSkewed, onCheck }: DeviceClockDialogProps) {
  const t = useTranslations('layout.deviceClock');
  const synced = status === 'synced';
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/60" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[101] max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-[460px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-border-primary bg-surface-primary p-6 text-text-primary shadow-xl focus:outline-none sm:p-8">
          <div
            className="mb-5 flex size-12 items-center justify-center rounded-full border border-highlight text-highlight"
            aria-hidden="true"
          >
            <svg aria-hidden="true" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <Dialog.Title className="text-2xl font-semibold leading-tight">{t(synced ? 'syncedTitle' : 'title')}</Dialog.Title>
          <Dialog.Description className="mt-3 text-base leading-relaxed text-text-secondary">
            {t(synced ? 'syncedDescription' : 'description')}
          </Dialog.Description>
          {!synced && (
            <ol className="my-6 list-decimal space-y-3 rounded-xl border border-border-primary bg-surface-secondary py-4 pl-10 pr-4 text-sm leading-relaxed">
              <li>{t('settings')}</li>
              <li>{t('automatic')}</li>
              <li>{t('sync')}</li>
            </ol>
          )}
          {(error || (!synced && stillSkewed)) && (
            <ErrorWithSupport as="p" role="alert" className="mb-4 text-sm leading-relaxed text-text-secondary">
              {t(error ? 'checkFailed' : 'stillSkewed')}
            </ErrorWithSupport>
          )}
          <div className="mt-6 flex flex-col gap-3">
            {synced ? (
              <Button type="button" className="w-full" onClick={() => onOpenChange(false)}>
                {t('continue')}
              </Button>
            ) : (
              <>
                <Button type="button" className="w-full" loading={checking} disabled={checking} onClick={onCheck}>
                  {t('checkAgain')}
                </Button>
                <Button type="button" variant="tertiary" className="w-full" onClick={() => onOpenChange(false)}>
                  {t('dismiss')}
                </Button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default function DeviceClockWarning() {
  const t = useTranslations('layout.deviceClock');
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<DeviceClockStatus>('unknown');
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(false);
  const [stillSkewed, setStillSkewed] = useState(false);
  const lastStatus = useRef<DeviceClockStatus>('unknown');
  const lastCheck = useRef(-Infinity);
  const request = useRef<AbortController | null>(null);

  const check = useCallback(
    async (manual = false) => {
      // Use monotonic time for throttling: correcting the device clock must
      // never prevent the next check. Coalesce focus/online/visibility events.
      if (request.current || (!manual && performance.now() - lastCheck.current < 60_000)) return;
      lastCheck.current = performance.now();
      const controller = new AbortController();
      request.current = controller;
      setChecking(true);
      setError(false);
      setStillSkewed(false);
      try {
        const next = await checkDeviceClock(controller.signal);
        if (controller.signal.aborted) return;
        if (next === 'skewed' && lastStatus.current !== 'skewed') setOpen(true);
        if (next === 'synced' && lastStatus.current === 'skewed') {
          // Retry read-only price data after syncing. Wallet approvals and
          // transactions still require the user's original explicit action.
          void queryClient.invalidateQueries({ queryKey: [QueryKeys.bitcoinPrice] });
        }
        lastStatus.current = next;
        setStatus(next);
        setStillSkewed(manual && next === 'skewed');
      } catch {
        // An offline/slow check cannot prove skew or clear a known mismatch.
        if (!controller.signal.aborted) setError(true);
      } finally {
        if (request.current === controller) {
          request.current = null;
          if (!controller.signal.aborted) setChecking(false);
        }
      }
    },
    [queryClient],
  );

  useEffect(() => {
    const checkVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    void check();
    const interval = setInterval(checkVisible, 5 * 60_000);
    window.addEventListener('focus', checkVisible);
    window.addEventListener('online', checkVisible);
    document.addEventListener('visibilitychange', checkVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', checkVisible);
      window.removeEventListener('online', checkVisible);
      document.removeEventListener('visibilitychange', checkVisible);
      request.current?.abort();
      request.current = null;
      lastCheck.current = -Infinity;
    };
  }, [check]);

  return (
    <>
      {status === 'skewed' && (
        <div
          role="status"
          className="mx-4 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-highlight bg-surface-secondary px-4 py-3 text-sm text-text-primary"
        >
          <span>{t('reminder')}</span>
          <Button type="button" size="small" variant="secondary" onClick={() => setOpen(true)}>
            {t('showHelp')}
          </Button>
        </div>
      )}
      <DeviceClockDialog
        open={open}
        onOpenChange={setOpen}
        status={status}
        checking={checking}
        error={error}
        stillSkewed={stillSkewed}
        onCheck={() => void check(true)}
      />
    </>
  );
}
